import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook para monitoramento da fila PGMQ de flows
 * 
 * Usa as RPCs criadas na migração Flow Engine 3.0 para consultar
 * estatísticas diretamente do PGMQ (filas q_flows e a_flows)
 */

/**
 * Interface para estatísticas da fila PGMQ
 */
export interface FlowQueueStatsPgmq {
  pending: number;
  processing: number;
  completed24h: number;
  failed24h: number;
  successRate: number;
  avgProcessingTime: number;
}

/**
 * Interface para item da fila PGMQ
 */
export interface FlowQueueItemPgmq {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: {
    tenant_id: string;
    flow_id: string;
    conversation_id: string;
    contact_id: string;
    inbox_id: string;
    provider_slug: string;
    action: string;
    payload: Record<string, unknown>;
    priority: number;
    max_attempts: number;
    enqueued_at: string;
  };
  // Campos enriquecidos
  tenant_name?: string;
  flow_name?: string;
  contact_phone?: string;
  status?: 'pending' | 'processing' | 'archived';
}

/**
 * Interface para volume por hora
 */
export interface FlowQueueVolumePgmq {
  hour: string;
  completed: number;
  failed: number;
}

/**
 * Interface para distribuição por action
 */
export interface FlowQueueByAction {
  action: string;
  count: number;
}

/**
 * Interface para distribuição por provider
 */
export interface FlowQueueByProvider {
  provider_slug: string;
  count: number;
}

/**
 * Interface de resposta da RPC get_flow_queue_stats_pgmq
 */
interface FlowQueueStatsRpcResponse {
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  avg_latency_ms: number;
}

/**
 * Hook para buscar estatísticas da fila PGMQ
 * Usa RPC get_flow_queue_stats_pgmq para dados precisos
 */
export function useFlowQueueStatsPgmq() {
  return useQuery({
    queryKey: ['flow-queue-stats-pgmq'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_flow_queue_stats_pgmq');
      
      if (error) throw error;
      
      // RPC retorna array com um elemento
      const stats = (data as unknown as FlowQueueStatsRpcResponse[])?.[0];
      
      if (!stats) {
        return {
          pending: 0,
          processing: 0,
          completed24h: 0,
          failed24h: 0,
          successRate: 100,
          avgProcessingTime: 0
        } as FlowQueueStatsPgmq;
      }
      
      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 
        ? (stats.completed_count / total) * 100 
        : 100;
      
      return {
        pending: stats.pending_count || 0,
        processing: stats.processing_count || 0,
        completed24h: stats.completed_count || 0,
        failed24h: stats.failed_count || 0,
        successRate,
        avgProcessingTime: (stats.avg_latency_ms || 0) / 1000 // converter ms para segundos
      } as FlowQueueStatsPgmq;
    },
    refetchInterval: 30000, // Auto-refresh a cada 30 segundos
  });
}

/**
 * Interface para item da RPC get_flow_queue_items_pgmq
 */
interface FlowQueueItemRpcResponse {
  id: string;
  queue_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  processed_at: string | null;
  error_message: string | null;
  payload: Record<string, unknown>;
  provider_slug: string | null;
  tenant_id: string | null;
  processing_time: number | null;
}

/**
 * Hook para buscar itens da fila PGMQ via RPC
 * Usa get_flow_queue_items_pgmq que combina q_flows e a_flows
 */
export function useFlowQueueItemsPgmq(
  statusFilter: string = 'all',
  providerFilter: string = 'all',
  limit: number = 100
) {
  return useQuery({
    queryKey: ['flow-queue-items-pgmq', statusFilter, providerFilter, limit],
    queryFn: async () => {
      // Buscar itens via RPC
      const { data, error } = await supabase.rpc('get_flow_queue_items_pgmq', {
        p_status: statusFilter,
        p_provider_slug: providerFilter,
        p_limit: limit
      });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as FlowQueueItemRpcResponse[];

      // Extrair IDs únicos para enriquecimento
      const tenantIds = new Set<string>();
      const flowIds = new Set<string>();
      const contactIds = new Set<string>();

      rawItems.forEach(item => {
        if (item.tenant_id) tenantIds.add(item.tenant_id);
        const payload = item.payload as Record<string, unknown>;
        const flowId = payload?.flow_id as string;
        const contactId = payload?.contact_id as string;
        if (flowId) flowIds.add(flowId);
        if (contactId) contactIds.add(contactId);
      });

      // Buscar dados enriquecidos em paralelo
      const [tenantsResult, flowsResult, contactsResult] = await Promise.all([
        tenantIds.size > 0
          ? supabase.from('tenants').select('id, name').in('id', Array.from(tenantIds))
          : Promise.resolve({ data: [] }),
        flowIds.size > 0
          ? supabase.from('flows').select('id, name').in('id', Array.from(flowIds))
          : Promise.resolve({ data: [] }),
        contactIds.size > 0
          ? supabase.from('contacts').select('id, phone').in('id', Array.from(contactIds))
          : Promise.resolve({ data: [] }),
      ]);

      // Criar Maps para lookup O(1)
      const tenantMap = new Map<string, string>(
        (tenantsResult.data || []).map((t: { id: string; name: string }) => [t.id, t.name])
      );
      const flowMap = new Map<string, string>(
        (flowsResult.data || []).map((f: { id: string; name: string }) => [f.id, f.name])
      );
      const contactMap = new Map<string, string>(
        (contactsResult.data || []).map((c: { id: string; phone: string | null }) => [c.id, c.phone || ''])
      );

      // Enriquecer itens
      return rawItems.map(item => {
        const payload = item.payload as Record<string, unknown>;
        const flowId = payload?.flow_id as string;
        const contactId = payload?.contact_id as string;
        const initialVariables = payload?.initial_variables as Record<string, unknown>;

        return {
          id: item.id,
          queue_type: item.queue_type || 'unknown',
          status: item.status,
          attempts: item.attempts || 0,
          max_attempts: item.max_attempts || 3,
          created_at: item.created_at,
          processed_at: item.processed_at,
          error_message: item.error_message,
          payload: item.payload,
          provider_slug: item.provider_slug,
          tenant_id: item.tenant_id,
          tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
          flow_name: flowId ? flowMap.get(flowId) || 'N/A' : 'N/A',
          contact_phone: (initialVariables?.contact_phone as string) || (contactId ? contactMap.get(contactId) || '' : ''),
          processing_time: item.processing_time || undefined,
        };
      });
    },
    refetchInterval: 30000,
  });
}

/**
 * Interface de resposta da RPC get_flow_queue_volume_pgmq
 */
interface FlowQueueVolumeRpcResponse {
  hour_label: string;
  completed_count: number;
  failed_count: number;
}

/**
 * Hook para buscar volume de processamento por hora
 * Usa RPC get_flow_queue_volume_pgmq
 */
export function useFlowQueueVolumePgmq(hours: number = 24) {
  return useQuery({
    queryKey: ['flow-queue-volume-pgmq', hours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_flow_queue_volume_pgmq', {
        hours_ago: hours
      });
      
      if (error) throw error;
      
      return ((data || []) as unknown as FlowQueueVolumeRpcResponse[]).map((item) => ({
        hour: item.hour_label,
        completed: item.completed_count || 0,
        failed: item.failed_count || 0
      })) as FlowQueueVolumePgmq[];
    },
    refetchInterval: 60000,
  });
}

/**
 * Interface de resposta da RPC get_flow_queue_by_action_pgmq
 */
interface FlowQueueByActionRpcResponse {
  action_type: string;
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
}

/**
 * Hook para buscar distribuição por action
 * Usa RPC get_flow_queue_by_action_pgmq
 */
export function useFlowQueueByActionPgmq() {
  return useQuery({
    queryKey: ['flow-queue-by-action-pgmq'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_flow_queue_by_action_pgmq');
      
      if (error) throw error;
      
      return (data as unknown as FlowQueueByActionRpcResponse[] || []).map(item => ({
        action: item.action_type,
        count: item.pending_count + item.processing_count + item.completed_count + item.failed_count
      })) as FlowQueueByAction[];
    },
    refetchInterval: 30000,
  });
}

/**
 * Interface de resposta da RPC get_flow_queue_by_provider_pgmq
 */
interface FlowQueueByProviderRpcResponse {
  provider_slug: string;
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
}

/**
 * Hook para buscar distribuição por provider
 * Usa RPC get_flow_queue_by_provider_pgmq
 */
export function useFlowQueueByProviderPgmq() {
  return useQuery({
    queryKey: ['flow-queue-by-provider-pgmq'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_flow_queue_by_provider_pgmq');
      
      if (error) throw error;
      
      return (data as unknown as FlowQueueByProviderRpcResponse[] || []).map(item => ({
        provider_slug: item.provider_slug,
        count: item.pending_count + item.processing_count + item.completed_count + item.failed_count
      })) as FlowQueueByProvider[];
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para gerar alertas de saúde da fila PGMQ
 */
export function useFlowQueueAlertsPgmq() {
  return useQuery({
    queryKey: ['flow-queue-alerts-pgmq'],
    queryFn: async () => {
      const alerts: { id: string; type: 'warning' | 'error'; message: string; count?: number }[] = [];
      
      // Buscar estatísticas
      const { data: statsData } = await supabase.rpc('get_flow_queue_stats_pgmq');
      const stats = (statsData as unknown as FlowQueueStatsRpcResponse[])?.[0];
      
      if (!stats) return alerts;
      
      // Alerta de itens pendentes altos
      if (stats.pending_count > 100) {
        alerts.push({
          id: 'high-pending',
          type: 'warning',
          message: `${stats.pending_count} itens pendentes na fila`,
          count: stats.pending_count
        });
      }
      
      // Alerta de itens em processamento por muito tempo
      if (stats.processing_count > 50) {
        alerts.push({
          id: 'high-processing',
          type: 'warning',
          message: `${stats.processing_count} itens em processamento (podem estar stuck)`,
          count: stats.processing_count
        });
      }
      
      // Alerta de taxa de sucesso baixa
      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 ? (stats.completed_count / total) * 100 : 100;
      
      if (successRate < 90 && total > 10) {
        alerts.push({
          id: 'low-success-rate',
          type: 'error',
          message: `Taxa de sucesso baixa: ${successRate.toFixed(1)}%`,
          count: stats.failed_count
        });
      }
      
      // Alerta de latência alta (>30 segundos = 30000ms)
      if (stats.avg_latency_ms > 30000) {
        alerts.push({
          id: 'high-latency',
          type: 'warning',
          message: `Latência média alta: ${(stats.avg_latency_ms / 1000).toFixed(0)}s`
        });
      }
      
      return alerts;
    },
    refetchInterval: 30000,
  });
}

// ============================================================================
// TOP 20 - ITENS COM MAIOR TEMPO DE EXECUÇÃO
// ============================================================================

/**
 * Interface de resposta da RPC get_flow_queue_slowest_items_pgmq
 */
interface FlowQueueSlowestItemRpcResponse {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  status: string;
  tenant_id: string | null;
  message: Record<string, unknown>;
  source: string;
}

/**
 * Interface pública para uso no componente
 */
export interface FlowQueueSlowestItem {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  processing_time_formatted: string;
  status: 'pending' | 'processing' | 'archived';
  tenant_id: string | null;
  tenant_name?: string;
  phone?: string;
  provider_slug?: string | null;
  action?: string;
  flow_name?: string | null;
  source: 'queue' | 'archive';
  message?: Record<string, unknown>;
}

/**
 * Formata tempo de processamento em milissegundos para leitura humana
 */
function formatProcessingTimeMs(ms: number): string {
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Hook para buscar os N itens com maior tempo de execução da fila PGMQ de flows
 * Usa RPC get_flow_queue_slowest_items_pgmq
 */
export function useFlowQueueSlowestItemsPgmq(limit: number = 20) {
  return useQuery({
    queryKey: ['flow-queue-slowest-pgmq', limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_flow_queue_slowest_items_pgmq', {
        p_limit: limit
      });
      
      if (error) throw error;
      if (!data || data.length === 0) return [];
      
      const rawItems = data as unknown as FlowQueueSlowestItemRpcResponse[];
      
      // Extrair IDs únicos para enriquecimento
      const tenantIds = new Set<string>();
      const flowIds = new Set<string>();
      
      rawItems.forEach(item => {
        if (item.tenant_id) tenantIds.add(item.tenant_id);
        const flowId = item.message?.flow_id as string;
        if (flowId) flowIds.add(flowId);
      });

      // Buscar dados enriquecidos em paralelo
      const [tenantsResult, flowsResult] = await Promise.all([
        tenantIds.size > 0
          ? supabase.from('tenants').select('id, name').in('id', Array.from(tenantIds))
          : Promise.resolve({ data: [] }),
        flowIds.size > 0
          ? supabase.from('flows').select('id, name').in('id', Array.from(flowIds))
          : Promise.resolve({ data: [] }),
      ]);

      // Criar Maps para lookup O(1)
      const tenantMap = new Map<string, string>(
        (tenantsResult.data || []).map((t: { id: string; name: string }) => [t.id, t.name])
      );
      const flowMap = new Map<string, string>(
        (flowsResult.data || []).map((f: { id: string; name: string }) => [f.id, f.name])
      );
      
      // Enriquecer e retornar itens
      return rawItems.map(item => ({
        msg_id: item.msg_id,
        read_ct: item.read_ct || 0,
        enqueued_at: item.enqueued_at,
        completed_at: item.completed_at,
        processing_time_ms: item.processing_time_ms,
        processing_time_formatted: formatProcessingTimeMs(item.processing_time_ms),
        status: item.status as 'pending' | 'processing' | 'archived',
        tenant_id: item.tenant_id,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
        phone: (item.message?.contact_phone as string) || '',
        provider_slug: (item.message?.provider_slug as string) || null,
        action: (item.message?.action as string) || 'N/A',
        flow_name: flowMap.get(item.message?.flow_id as string) || null,
        source: item.source as 'queue' | 'archive',
        message: item.message || {},
      })) as FlowQueueSlowestItem[];
    },
    refetchInterval: 30000,
  });
}
